import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import D3CandlestickChart from './D3CandlestickChart';
import './SocialBuzzDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const SocialBuzzDashboard = () => {
  const [buzzScans, setBuzzScans] = useState([]);
  const [selectedBuzz, setSelectedBuzz] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedChart, setSelectedChart] = useState(null);
  const [chartModal, setChartModal] = useState(null);  // For expanded view
  const [livePrices, setLivePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(false);
  const [optionPrices, setOptionPrices] = useState({});
  const [optionPricesLoading, setOptionPricesLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

  // Fetch available buzz scans
  const fetchBuzzScans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/buzz-scans`);
      const sortedScans = response.data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); setBuzzScans(sortedScans);

      // Auto-select latest scan if none selected
      if (!selectedBuzz && response.data.length > 0) {
        await loadBuzzScan(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching buzz scans:', error);
    }
  };

  // Fetch current live prices for symbols (stock prices)
  const fetchLivePrices = async (symbols) => {
    if (!symbols || symbols.length === 0) return;

    setPricesLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/current-prices`, { symbols });
      if (response.data) {
        setLivePrices(response.data);
      }
    } catch (error) {
      console.error('Error fetching live prices:', error);
    } finally {
      setPricesLoading(false);
    }
  };

  // Fetch current option prices for options strategies
  const fetchOptionPrices = async (stocks) => {
    if (!stocks || stocks.length === 0) return;

    // Filter to only options strategies with strike and expiry
    const optionStocks = stocks.filter(s =>
      isOptionsStrategy(s.strategy) && s.strike && s.expiry
    );

    if (optionStocks.length === 0) return;

    setOptionPricesLoading(true);
    try {
      const options = optionStocks.map(s => ({
        symbol: s.symbol,
        strike: s.strike,
        expiry: s.expiry,
        type: s.strategy === 'BUY_PUTS' ? 'PUT' : 'CALL'
      }));

      const response = await axios.post(`${API_URL}/api/option-prices`, { options });
      if (response.data) {
        setOptionPrices(response.data);
      }
    } catch (error) {
      console.error('Error fetching option prices:', error);
    } finally {
      setOptionPricesLoading(false);
    }
  };

  // Load specific buzz scan data
  const loadBuzzScan = async (buzzId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/buzz-scans/${buzzId}`);
      setCurrentData(response.data);
      setSelectedBuzz(buzzId);

      // Fetch live prices for all symbols in the scan
      if (response.data?.top_buzz?.length > 0) {
        const stocks = response.data.top_buzz;
        const symbols = stocks.map(s => s.symbol);

        // Fetch stock prices for non-options strategies
        await fetchLivePrices(symbols);

        // Fetch current option prices (for Current column)
        // Entry prices are pre-computed during enrichment (using Polygon API)
        await fetchOptionPrices(stocks);
      }
    } catch (error) {
      console.error('Error loading buzz scan:', error);
    } finally {
      setLoading(false);
    }
  };

  // Run social buzz scan
  const runBuzzScan = async () => {
    setScanRunning(true);
    setScanProgress('Fetching trending tickers...');

    try {
      const response = await axios.post(`${API_URL}/api/run-social-buzz-scan`);

      if (response.data.success) {
        setScanProgress('Scan complete! Loading results...');

        // Refresh the scans list
        await fetchBuzzScans();

        // Load the new scan
        if (response.data.buzzId) {
          await loadBuzzScan(response.data.buzzId);
        }

        setScanProgress('Complete!');
        setTimeout(() => {
          setScanRunning(false);
          setScanProgress('');
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Scan failed');
      }
    } catch (error) {
      console.error('Error running buzz scan:', error);
      setScanProgress('Scan failed');
      setTimeout(() => {
        setScanRunning(false);
        setScanProgress('');
      }, 3000);
    }
  };

  // Initial load
  useEffect(() => {
    fetchBuzzScans();
  }, []);

  // Auto-refresh prices every 60 seconds when market is open
  useEffect(() => {
    if (!currentData?.top_buzz?.length) return;

    const stocks = currentData.top_buzz;
    const symbols = stocks.map(s => s.symbol);

    // Initial fetch
    fetchLivePrices(symbols);
    fetchOptionPrices(stocks);

    // Set up interval for periodic refresh
    const interval = setInterval(() => {
      fetchLivePrices(symbols);
      fetchOptionPrices(stocks);
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [currentData]);

  const formatVolume = (volume) => {
    if (!volume) return '-';
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(2)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(0)}K`;
    return volume.toString();
  };

  const formatPercent = (percent) => {
    if (!percent && percent !== 0) return '-';
    const sign = percent > 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return `$${price.toFixed(2)}`;
  };

  // Get current price for a symbol - prefer live price, fallback to scan price
  const getCurrentPrice = (stock) => {
    const symbol = stock.symbol;
    // livePrices format: { SYMBOL: { last_price: X, ... } }
    if (livePrices[symbol]?.last_price) {
      return livePrices[symbol].last_price;
    }
    // Fallback to scan price
    return stock.price;
  };

  // Get open price for a symbol - prefer live price, fallback to scan data
  const getOpenPrice = (stock) => {
    const symbol = stock.symbol;
    // livePrices format: { SYMBOL: { open_price: X, ... } }
    if (livePrices[symbol]?.open_price) {
      return livePrices[symbol].open_price;
    }
    // Fallback to scan data
    return stock.open_price;
  };

  // Check if strategy is an options trade
  const isOptionsStrategy = (strategy) => {
    return ['BUY_CALLS', 'BUY_PUTS', 'STRADDLE'].includes(strategy);
  };

  // Get strike price - use data from scan, or calculate if not available
  const getStrikePrice = (stock) => {
    if (!isOptionsStrategy(stock.strategy)) return null;

    // Use strike from scan data if available
    if (stock.strike) return stock.strike;

    // Calculate if not available
    const price = stock.price || stock.entry;
    if (!price) return null;

    // Round to nearest $0.50 for stocks under $10, $1 for under $50, $5 for higher
    if (price < 10) {
      return Math.round(price * 2) / 2;
    } else if (price < 50) {
      return Math.round(price);
    } else {
      return Math.round(price / 5) * 5;
    }
  };

  // Get expiry date - use data from scan, or calculate if not available
  const getExpiryDate = (stock) => {
    if (!isOptionsStrategy(stock.strategy)) return null;

    // Use expiry from scan data if available
    if (stock.expiry) {
      // Format as "Dec 27" style
      const expDate = new Date(stock.expiry);
      return expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Calculate if not available
    if (!currentData?.timestamp) return null;

    const scanDate = new Date(currentData.timestamp);
    const expiry = new Date(scanDate);
    expiry.setDate(expiry.getDate() + 21); // 3 weeks out

    // Find next Friday
    const dayOfWeek = expiry.getDay();
    const daysToFriday = (5 - dayOfWeek + 7) % 7;
    expiry.setDate(expiry.getDate() + (daysToFriday === 0 ? 7 : daysToFriday));

    return expiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get entry price for display - stock price for stocks, option price for options
  // Entry price is pre-computed during enrichment using Polygon API historical data
  const getEntryPrice = (stock) => {
    if (isOptionsStrategy(stock.strategy)) {
      // For options, use option_entry from scan data (historical price from Polygon at scan time)
      return stock.option_entry || stock.entry;
    }
    // For stocks, use stock entry price
    return stock.entry;
  };

  // Get current price for display - stock price for stocks, option price for options
  const getDisplayCurrentPrice = (stock) => {
    if (isOptionsStrategy(stock.strategy)) {
      // For options, try to get live option price
      const strike = getStrikePrice(stock);
      const expiry = stock.expiry;
      const optType = stock.strategy === 'BUY_PUTS' ? 'PUT' : 'CALL';

      if (strike && expiry) {
        const key = `${stock.symbol}_${strike}_${expiry}_${optType}`;
        const optData = optionPrices[key];
        if (optData) {
          // Prefer last price (actual trade) over mark (mid of bid/ask)
          // Use mark as fallback if last is 0
          return optData.last > 0 ? optData.last : (optData.mark || optData.last);
        }
      }
      // Fall back to option_entry (estimated entry) if no live price
      return stock.option_entry || stock.entry;
    }
    // For stocks, use live stock price
    return getCurrentPrice(stock);
  };
  // Handle column sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Calculate P&L for a stock (used for sorting)
  const calculatePnL = (stock) => {
    const entry = getEntryPrice(stock);
    const isClosed = stock.status === 'CLOSED' || (stock.exit_price && stock.exit_reason && stock.exit_reason !== 'STILL_OPEN');
    const exitOrCurrent = isClosed ? stock.exit_price : getDisplayCurrentPrice(stock);
    if (entry && exitOrCurrent) {
      return ((exitOrCurrent - entry) / entry * 100);
    }
    return null;
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!data || !sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'pnl': aVal = calculatePnL(a); bVal = calculatePnL(b); break;
        case 'buzz': aVal = a.buzz_score; bVal = b.buzz_score; break;
        case 'open_pct': aVal = a.change_pct; bVal = b.change_pct; break;
        case 'volume': aVal = a.volume; bVal = b.volume; break;
        default: return 0;
      }
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal;
    });
  };


  const getSentimentColor = (bullishPct) => {
    if (bullishPct > 70) return '#10b981';
    if (bullishPct > 50) return '#6ee7b7';
    if (bullishPct < 30) return '#ef4444';
    if (bullishPct < 50) return '#fca5a5';
    return '#9ca3af';
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'HIGH': return '#10b981';
      case 'MEDIUM': return '#fbbf24';
      case 'SPECULATIVE': return '#f97316';
      case 'LOW': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStrategyBadge = (strategy) => {
    if (!strategy || strategy === 'NO_TRADE') return null;

    const strategyColors = {
      'BUY_CALLS': { bg: '#10b981', text: 'CALLS' },
      'BUY_PUTS': { bg: '#ef4444', text: 'PUTS' },
      'STRADDLE': { bg: '#8b5cf6', text: 'STRADDLE' },
      'MOMENTUM_LONG': { bg: '#10b981', text: 'LONG' },
      'MOMENTUM_SHORT': { bg: '#ef4444', text: 'SHORT' },
      'SQUEEZE_PLAY': { bg: '#f97316', text: 'SQUEEZE' },
      'EARNINGS_PLAY': { bg: '#3b82f6', text: 'EARNINGS' },
      'FDA_PLAY': { bg: '#ec4899', text: 'FDA' },
      'WATCH_FOR_REVERSAL': { bg: '#6b7280', text: 'WATCH' }
    };

    const config = strategyColors[strategy] || { bg: '#6b7280', text: strategy };

    return (
      <span style={{
        background: config.bg,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: '600'
      }}>
        {config.text}
      </span>
    );
  };

  // Handle entry price click to show chart from scan date to current
  const handleEntryClick = async (e, stock) => {
    e.stopPropagation();

    // Show loading state in right sidebar
    setSelectedChart({
      symbol: stock.symbol,
      loading: true,
      currentPrice: stock.price
    });

    try {
      // Get scan date as start date for the chart range
      const scanTime = currentData?.timestamp ? new Date(currentData.timestamp) : new Date();
      const scanDateStr = scanTime.toISOString().split('T')[0];

      // Fetch chart data from scan date to now (5-min if recent, daily if older)
      const response = await axios.get(`${API_URL}/api/chart-range/${stock.symbol}?startDate=${scanDateStr}`);
      const chartData = response.data;

      if (!chartData.candles || chartData.candles.length === 0) {
        setSelectedChart({
          symbol: stock.symbol,
          currentPrice: stock.price,
          prevClose: stock.prev_close || stock.price * 0.95,
          loading: false,
          error: true,
          errorMessage: 'No chart data available'
        });
      } else {
        // Compute high/low from candles if not provided
        let rangeHigh = chartData.dayHigh;
        let rangeLow = chartData.dayLow;
        if ((!rangeHigh || !rangeLow) && chartData.candles.length > 0) {
          rangeHigh = Math.max(...chartData.candles.map(c => c.high));
          rangeLow = Math.min(...chartData.candles.map(c => c.low));
        }

        // Get entry price (first candle's open)
        const entryPrice = chartData.candles[0]?.open || stock.entry || stock.price;

        setSelectedChart({
          symbol: chartData.symbol || stock.symbol,
          candles: chartData.candles || [],
          currentPrice: chartData.currentPrice || stock.price,
          previousClose: chartData.previousClose || stock.prev_close,
          dayHigh: rangeHigh,
          dayLow: rangeLow,
          volume: chartData.volume,
          frequency: chartData.frequency,
          entryPrice: entryPrice,
          scanDate: scanDateStr,
          loading: false
        });
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setSelectedChart({
        symbol: stock.symbol,
        currentPrice: stock.price,
        prevClose: stock.prev_close || stock.price * 0.95,
        loading: false,
        error: true
      });
    }
  };

  // Get actionable trades count
  const actionableTrades = currentData?.top_buzz?.filter(s => s.strategy && s.strategy !== 'NO_TRADE') || [];

  return (
    <div className="social-buzz-dashboard">
      {/* Header */}
      <div className="buzz-header">
        <div className="buzz-title">Social Buzz - StockTwits Analysis</div>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-label">Total Analyzed</span>
            <span className="stat-value">{currentData?.total_scanned || 0}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Actionable</span>
            <span className="stat-value" style={{ color: '#10b981' }}>{actionableTrades.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Scan Time</span>
            <span className="stat-value">
              {currentData?.timestamp ? format(new Date(currentData.timestamp), 'HH:mm:ss') : '-'}
            </span>
          </div>
          <button
            className="refresh-prices-btn"
            onClick={() => currentData?.top_buzz && fetchLivePrices(currentData.top_buzz.map(s => s.symbol))}
            disabled={pricesLoading}
            style={{
              padding: '8px 16px',
              background: pricesLoading ? '#4b5563' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: pricesLoading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              marginLeft: '16px'
            }}
          >
            {pricesLoading ? 'Refreshing...' : '↻ Refresh Prices'}
          </button>
        </div>
      </div>

      <div className="buzz-content">
        {/* Left Sidebar */}
        <div className="buzz-sidebar">
          <div className="panel">
            <div className="panel-header">
              <h3>Scan History</h3>
            </div>
            <div className="panel-content">
              <button
                className={`run-scan-btn ${scanRunning ? 'running' : ''}`}
                onClick={runBuzzScan}
                disabled={scanRunning}
              >
                {scanRunning ? 'Scanning...' : 'Run Buzz Scan'}
              </button>
              {scanRunning && (
                <div className="scan-progress">
                  <div className="progress-bar">
                    <div className="progress-bar-fill"></div>
                  </div>
                  <div className="progress-text">{scanProgress}</div>
                </div>
              )}
              <select
                className="scan-selector"
                value={selectedBuzz || ''}
                onChange={(e) => e.target.value && loadBuzzScan(e.target.value)}
              >
                <option value="">Select a scan...</option>
                {buzzScans.map((scan) => (
                  <option key={scan.id} value={scan.id}>
                    {scan.timestamp ? new Date(scan.timestamp).toLocaleString() : scan.filename}
                  </option>
                ))}
              </select>
              {selectedBuzz && currentData && (
                <div className="scan-info">
                  <div className="scan-info-item">
                    <span>Time:</span>
                    <strong>{currentData.timestamp ? new Date(currentData.timestamp).toLocaleString() : 'N/A'}</strong>
                  </div>
                  <div className="scan-info-item">
                    <span>Tickers:</span>
                    <strong>{currentData.total_scanned || 0}</strong>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Top Recommendations */}
          <div className="panel">
            <div className="panel-header">
              <h3>Top Trades</h3>
            </div>
            <div className="panel-content">
              {actionableTrades.slice(0, 5).map((stock, i) => (
                <div key={i} className="top-buzz-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="buzz-rank">#{i + 1}</span>
                      <span className="buzz-symbol">{stock.symbol}</span>
                    </div>
                    {getStrategyBadge(stock.strategy)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    {stock.confidence} • {formatPrice(stock.entry)} → {formatPrice(stock.target)}
                  </div>
                </div>
              ))}
              {actionableTrades.length === 0 && (
                <div style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                  No actionable trades in current scan
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="buzz-main">
          <div className="panel">
            <div className="panel-header">
              <h3>Trending Tickers</h3>
            </div>
            <div className="buzz-table-container">
              {loading ? (
                <div className="loading">Loading...</div>
              ) : (
                <table className="buzz-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Symbol</th>
                      <th>Open</th>
                      <th onClick={() => handleSort('open_pct')} style={{ cursor: 'pointer' }}>
                        Open % {sortConfig.key === 'open_pct' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('buzz')} style={{ cursor: 'pointer' }}>
                        Buzz {sortConfig.key === 'buzz' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                      </th>
                      <th>Sentiment</th>
                      <th>Strategy</th>
                      <th>Strike</th>
                      <th>Expiry</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th>Current</th>
                      <th>Status</th>
                      <th onClick={() => handleSort('pnl')} style={{ cursor: 'pointer' }}>
                        P&L {sortConfig.key === 'pnl' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                      </th>
                      <th onClick={() => handleSort('volume')} style={{ cursor: 'pointer' }}>
                        Volume {sortConfig.key === 'volume' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedData(currentData?.top_buzz)?.map((stock, index) => (
                      <React.Fragment key={stock.symbol}>
                        <tr
                          onClick={() => setExpandedRow(expandedRow === stock.symbol ? null : stock.symbol)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td className="rank-cell">#{index + 1}</td>
                          <td className="symbol-cell">{stock.symbol}</td>
                          {/* Open - day's open price */}
                          <td>
                            {getOpenPrice(stock) ? formatPrice(getOpenPrice(stock)) : '-'}
                          </td>
                          {/* Open % - % from open to current (or open to close if market closed) */}
                          <td className={
                            getOpenPrice(stock) && getCurrentPrice(stock)
                              ? ((getCurrentPrice(stock) - getOpenPrice(stock)) / getOpenPrice(stock) * 100) >= 0 ? 'gain-positive' : 'gain-negative'
                              : (stock.change_today != null ? (stock.change_today >= 0 ? 'gain-positive' : 'gain-negative') : '')
                          }>
                            {getOpenPrice(stock) && getCurrentPrice(stock)
                              ? formatPercent((getCurrentPrice(stock) - getOpenPrice(stock)) / getOpenPrice(stock) * 100)
                              : (stock.change_today != null ? formatPercent(stock.change_today) : '-')
                            }
                          </td>
                          <td className="buzz-score-cell">
                            <div className="buzz-score-bar">
                              <div
                                className="buzz-score-fill"
                                style={{
                                  width: `${stock.buzz_score}%`,
                                  backgroundColor: stock.buzz_score > 70 ? '#10b981' : stock.buzz_score > 50 ? '#fbbf24' : '#9ca3af'
                                }}
                              ></div>
                              <span className="buzz-score-text">{stock.buzz_score}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div className="sentiment-bar">
                                <div className="sentiment-fill bullish" style={{ width: `${stock.bullish_pct}%` }}></div>
                                <div className="sentiment-fill bearish" style={{ width: `${stock.bearish_pct}%` }}></div>
                              </div>
                              <span style={{ color: getSentimentColor(stock.bullish_pct), fontSize: '12px' }}>
                                {stock.bullish_pct?.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                          <td>{getStrategyBadge(stock.strategy) || <span style={{ color: '#6b7280' }}>-</span>}</td>
                          <td className="strike-cell">
                            {getStrikePrice(stock) ? (
                              <div className="strike-with-tooltip">
                                <span style={{ color: '#fbbf24' }}>${getStrikePrice(stock)}</span>
                                {isOptionsStrategy(stock.strategy) && (
                                  <div className="underlying-tooltip">
                                    <div className="tooltip-header">Underlying Stock</div>
                                    <div className="tooltip-row">
                                      <span>Entry:</span>
                                      <span>${stock.underlying_entry?.toFixed(2) || stock.price?.toFixed(2) || '-'}</span>
                                    </div>
                                    {stock.underlying_exit && (
                                      <div className="tooltip-row">
                                        <span>Exit:</span>
                                        <span>${stock.underlying_exit?.toFixed(2)}</span>
                                      </div>
                                    )}
                                    <div className="tooltip-row">
                                      <span>Current:</span>
                                      <span>${(livePrices[stock.symbol]?.last_price || stock.underlying_current || stock.price)?.toFixed(2) || '-'}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#6b7280' }}>-</span>
                            )}
                          </td>
                          <td>
                            {getExpiryDate(stock) ? (
                              <span style={{ color: '#a78bfa' }}>{getExpiryDate(stock)}</span>
                            ) : (
                              <span style={{ color: '#6b7280' }}>-</span>
                            )}
                          </td>
                          {/* Entry - price at scan time */}
                          <td>
                            {getEntryPrice(stock) ? (
                              <div className="entry-with-tooltip">
                                <span style={{ color: '#3b82f6' }}>
                                  {formatPrice(getEntryPrice(stock))}
                                  {isOptionsStrategy(stock.strategy) && <span style={{ fontSize: '9px', color: '#8b5cf6', marginLeft: '2px' }}>OPT</span>}
                                </span>
                                <div className="underlying-tooltip">
                                  <div className="tooltip-row">
                                    <span>{currentData?.timestamp ? `${new Date(currentData.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${new Date(currentData.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : '-'}</span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: '#6b7280' }}>-</span>
                            )}
                          </td>
                          {/* Exit */}
                          <td>
                            {stock.exit_price ? (
                              <div className="exit-with-tooltip">
                                <span style={{ color: '#f59e0b' }}>
                                  {formatPrice(stock.exit_price)}
                                  {isOptionsStrategy(stock.strategy) && <span style={{ fontSize: '9px', color: '#8b5cf6', marginLeft: '2px' }}>OPT</span>}
                                </span>
                                <div className="underlying-tooltip">
                                  <div className="tooltip-row">
                                    <span>{stock.exit_date ? `${new Date(stock.exit_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${new Date(stock.exit_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : '-'}</span>
                                  </div>
                                  {stock.exit_reason && (
                                    <div className="tooltip-row" style={{ marginTop: '4px', color: '#9ca3af', fontSize: '11px' }}>
                                      <span>{stock.exit_reason.replace(/_/g, ' ')}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: '#6b7280' }}>-</span>
                            )}
                          </td>
                          {/* Current - live option price for options, live stock price for stocks */}
                          <td
                            className="clickable-price"
                            onClick={(e) => handleEntryClick(e, stock)}
                            style={{ cursor: 'pointer' }}
                          >
                            {formatPrice(getDisplayCurrentPrice(stock))}
                            {isOptionsStrategy(stock.strategy) && <span style={{ fontSize: '9px', color: '#8b5cf6', marginLeft: '2px' }}>OPT</span>}
                            {(pricesLoading || optionPricesLoading) && <span style={{ marginLeft: '4px', color: '#6b7280' }}>...</span>}
                          </td>
                          <td>
                            {stock.status === 'CLOSED' ? (
                              <span style={{
                                background: '#10b981',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>CLOSED</span>
                            ) : stock.strategy && stock.strategy !== 'NO_TRADE' && stock.strategy !== 'WATCH' ? (
                              <span style={{
                                background: '#3b82f6',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>OPEN</span>
                            ) : (
                              <span style={{ color: '#6b7280' }}>-</span>
                            )}
                          </td>
                          {/* P&L - entry to exit (if closed) or entry to current (if open) */}
                          <td className={
                            (() => {
                              // Use getEntryPrice for entry (option_entry for options, stock entry for stocks)
                              const entry = getEntryPrice(stock);
                              // Use exit_price if closed, otherwise use getDisplayCurrentPrice (option price for options, stock price for stocks)
                              const exitOrCurrent = stock.exit_price || getDisplayCurrentPrice(stock);
                              if (entry && exitOrCurrent) {
                                return ((exitOrCurrent - entry) / entry * 100) >= 0 ? 'gain-positive' : 'gain-negative';
                              }
                              return '';
                            })()
                          }>
                            {(() => {
                              // Use getEntryPrice for entry (option_entry for options, stock entry for stocks)
                              const entry = getEntryPrice(stock);
                              // Use exit_price if closed, otherwise use getDisplayCurrentPrice (option price for options, stock price for stocks)
                              const exitOrCurrent = stock.exit_price || getDisplayCurrentPrice(stock);
                              if (entry && exitOrCurrent) {
                                return <span style={{ fontWeight: '600' }}>{formatPercent((exitOrCurrent - entry) / entry * 100)}</span>;
                              }
                              return <span style={{ color: '#6b7280' }}>-</span>;
                            })()}
                          </td>
                          <td>{formatVolume(stock.volume)}</td>
                        </tr>
                        {/* Expanded Row - AI Summary & Rationale */}
                        {expandedRow === stock.symbol && (stock.ai_summary || stock.rationale) && (
                          <tr>
                            <td colSpan={15} style={{ background: '#1a1a1f', padding: '16px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {stock.ai_summary && (
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#8b5cf6', fontWeight: '600', marginBottom: '4px' }}>
                                      AI SUMMARY
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#e4e4e7', lineHeight: '1.5' }}>
                                      {stock.ai_summary}
                                    </div>
                                  </div>
                                )}
                                {stock.rationale && (
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#fbbf24', fontWeight: '600', marginBottom: '4px' }}>
                                      RATIONALE
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                                      {stock.rationale}
                                    </div>
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: '#6b7280', flexWrap: 'wrap' }}>
                                  <span>Volatility: {stock.volatility?.toFixed(0)}%</span>
                                  <span>Vol Ratio: {stock.volume_ratio?.toFixed(1)}x</span>
                                  <span>Week: {formatPercent(stock.week_change)}</span>
                                  <span>Options: {stock.options_mentions || 0}</span>
                                  <span>Catalysts: {stock.catalyst_mentions || 0}</span>
                                  {stock.exit_reason && (
                                    <span style={{ color: '#f59e0b' }}>Exit: {stock.exit_reason.replace(/_/g, ' ')}</span>
                                  )}
                                  {stock.exit_date && (
                                    <span style={{ color: '#9ca3af' }}>Exit Date: {new Date(stock.exit_date).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Chart Panel (inside buzz-content) */}
        {selectedChart && (
          <div className="buzz-sidebar-right">
            <div className="panel">
              <div className="panel-header">
                <h3>
                  {selectedChart.symbol}
                  {selectedChart.frequency && <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>({selectedChart.frequency})</span>}
                </h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="expand-chart-btn"
                    onClick={() => setChartModal(selectedChart)}
                    title="Expand chart"
                    style={{
                      background: 'transparent',
                      border: '1px solid #3a3a3f',
                      color: '#9ca3af',
                      fontSize: '14px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    ⛶
                  </button>
                  <button
                    className="close-panel"
                    onClick={() => setSelectedChart(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#9ca3af',
                      fontSize: '18px',
                      cursor: 'pointer'
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="panel-content" style={{ padding: '12px' }}>
                {selectedChart.loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '250px', color: '#9ca3af' }}>
                    Loading chart...
                  </div>
                ) : selectedChart.error ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '250px', color: '#ef4444' }}>
                    {selectedChart.errorMessage || 'Failed to load chart data'}
                  </div>
                ) : (
                  <>
                    <div style={{ cursor: 'pointer' }} onClick={() => setChartModal(selectedChart)}>
                      <D3CandlestickChart chartData={selectedChart} />
                    </div>
                    {selectedChart.scanDate && (
                      <div style={{ marginTop: '12px', padding: '8px', background: '#1a1a1f', borderRadius: '6px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#6b7280' }}>Scan Date:</span>
                          <span style={{ color: '#ffffff' }}>{selectedChart.scanDate}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#6b7280' }}>Range High:</span>
                          <span style={{ color: '#10b981' }}>${selectedChart.dayHigh?.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>Range Low:</span>
                          <span style={{ color: '#ef4444' }}>${selectedChart.dayLow?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-left">
          <span className={`status-dot ${loading ? 'loading' : 'connected'}`}></span>
          <span>{loading ? 'Loading...' : 'Connected'}</span>
        </div>
        <div className="status-center">
          <span>Last Scan: {currentData?.timestamp ? format(new Date(currentData.timestamp), 'MMM dd, HH:mm:ss') : '-'}</span>
        </div>
        <div className="status-right">
          <span>{currentData?.total_scanned || 0} tickers | {actionableTrades.length} trades</span>
        </div>
      </div>

      {/* Price Chart Modal - from scan date to current */}
      {chartModal && (
        <div className="chart-modal-overlay" onClick={() => setChartModal(null)}>
          <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h2>
                {chartModal.symbol}
                {chartModal.scanDate && ` - ${chartModal.scanDate} to Now`}
                {chartModal.frequency && <span style={{ fontSize: '14px', color: '#9ca3af', marginLeft: '10px' }}>({chartModal.frequency})</span>}
              </h2>
              <button className="close-modal" onClick={() => setChartModal(null)}>
                ×
              </button>
            </div>
            <div className="chart-modal-content">
              {chartModal.loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#9ca3af' }}>
                  Loading chart...
                </div>
              ) : chartModal.error ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#ef4444' }}>
                  {chartModal.errorMessage || 'Failed to load chart data'}
                </div>
              ) : (
                <D3CandlestickChart chartData={chartModal} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialBuzzDashboard;
